import React, { type FC, useEffect } from 'react'

import { type AppVersions, NotificationSeverity, selectDifferingVersions, Translation } from '../../../domain'

import { useAsyncCallback } from '../Callback'
import { useTranslator } from '../Internationalization'
import { useIntervalEffect } from '../Interval'
import { useAppState, useAppUsecases } from '../State'

const VERSION_MISMATCH_DISMISS_KEY = 'version-mismatch'

/**
 * Migrated from a corner Snackbar to a session-scoped Banner
 * (lore/engineering/CC-4102-notifications/Notifications_Migration.plan.md Step 6) - rendering
 * itself now happens once, in the page layout, via `<BannerRenderer/>`
 * (Step 5). This component is purely the "invisible controller" driving `bannerUsecase`.
 */
export const Notifier: FC = () => {
    const differingVersions = useAppState(selectDifferingVersions, (v: AppVersions | null) => `${v?.ui || ''}_${v?.connectware || ''}`)
    const translator = useTranslator()

    const { bannerUsecase, fixVersionUsecase, versionUsecase } = useAppUsecases()

    /** Verify the version from the backend every couple of seconds */
    const [loadVersions] = useAsyncCallback(() => versionUsecase.loadVersions(), [versionUsecase])
    useIntervalEffect(loadVersions, 20_000, [versionUsecase])

    useEffect(() => {
        if (!differingVersions) {
            /** Lets the banner reappear if a mismatch recurs after having been dismissed. */
            bannerUsecase.clear(VERSION_MISMATCH_DISMISS_KEY)
            return
        }

        bannerUsecase.show({
            severity: NotificationSeverity.ERROR,
            title: translator.formatTranslation(Translation.VERSION_MISMATCH_TITLE),
            description: translator.formatTranslation(Translation.VERSION_MISMATCH_BODY, {
                backend: differingVersions.connectware,
                frontend: differingVersions.ui,
            }),
            action: {
                label: translator.formatTranslation(Translation.VERSION_MISMATCH_ACTION),
                onClick: () => fixVersionUsecase.invoke(),
            },
            dismissKey: VERSION_MISMATCH_DISMISS_KEY,
        })
    }, [differingVersions, bannerUsecase, fixVersionUsecase, translator])

    return <></>
}
